import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Group, Button, Badge, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  StatusBadge,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Switch, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { admin } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatDateTime, formatRelative } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { TenantName } from '../../../components/TenantName';
import { CopyableId } from '../../../components/CopyableId';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { TableFooter } from '../../../components/TableFooter';
import { RowMenu } from '../../../components/RowMenu';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { useTenantMap } from '../../../data/useTenants';
import { OneTimeSecret } from '../components/OneTimeSecret';
import { CreateKeyDrawer, type CreatedKey } from './components/CreateKeyDrawer';
import { RevokeSelectionDrawer } from './components/RevokeSelectionDrawer';
import { KeyDetailDrawer } from './components/KeyDetailDrawer';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
type Key = admin.AdminApiKey;
type KpiKey = 'all' | 'active' | 'revoked' | 'idle' | 'recent';

const KeyIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    <circle cx="8" cy="12" r="3.5" /><path d="M11.5 12H20M17 12v3M20 12v2.5" />
  </svg>
);

/** CSV cell escaping: a key name is free text and can hold a comma or a quote. */
function csvEscape(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function ApiKeysPage() {
  const t = useT();
  const qc = useQueryClient();
  const [showRevoked, setShowRevoked] = useState(false);

  /*
   * Revoked keys are always fetched. The page used to ask the server for
   * active keys only, then count "Revoked" over that list, so the KPI read 0
   * on load and clicking it landed on an empty table. The switch is now a
   * display filter over one honest set of counts.
   */
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'api-keys', { includeRevoked: true }],
    queryFn: () => admin.listApiKeys({ include_revoked: true })
  });
  const all = useMemo(() => (data?.data ?? []) as Key[], [data]);
  const { map: tenants } = useTenantMap();

  const revoke = useMutation({
    mutationFn: (id: string) => admin.revokeApiKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] })
  });

  const [view, setView] = usePersistedState<ViewMode>('admin.api-keys.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.api-keys.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Which key's provenance panel is open, by id. The detail is fetched when it
  // opens rather than carried on every list row: `use_count` and `last_used_ip`
  // are aggregates over the usage log and have no business in a list query.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  /*
   * The cutoffs used to be memoised on an empty dependency list, so a tab left
   * open overnight measured "idle 90 days" against yesterday's clock. They now
   * advance with every refetch, which is when the numbers can change anyway.
   */
  const clock = dataUpdatedAt || Date.now();
  const idleCutoff = clock - 90 * 86_400_000;
  const recentCutoff = clock - 7 * 86_400_000;

  const isIdle = (r: Key) => r.is_active && (!r.last_used_at || new Date(r.last_used_at).getTime() < idleCutoff);
  const isRecent = (r: Key) => Boolean(r.last_used_at && new Date(r.last_used_at).getTime() >= recentCutoff);

  const counts = {
    all: all.length,
    active: all.filter((r) => r.is_active).length,
    revoked: all.filter((r) => !r.is_active).length,
    idle: all.filter(isIdle).length,
    recent: all.filter(isRecent).length
  };

  /*
   * Each card carries its share of the whole as well as its count, because the
   * question an operator asks of "234 revoked" is "out of how many". A share
   * is undefined rather than 0% before any key has loaded.
   */
  const share = (n: number) => counts.all === 0
    ? undefined
    : t('settings.apiKeys.kpi.share', { percent: ((n / counts.all) * 100).toFixed(1) });

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('settings.apiKeys.kpi.all'), value: counts.all, tone: 'primary', icon: KeyIcon, hint: share(counts.all) },
    { id: 'active', label: t('status.active'), value: counts.active, tone: 'healthy', hint: share(counts.active) },
    { id: 'revoked', label: t('settings.apiKeys.status.revoked'), value: counts.revoked, tone: 'inactive', hint: share(counts.revoked) },
    { id: 'idle', label: t('settings.apiKeys.kpi.idle'), value: counts.idle, tone: 'warning', hint: share(counts.idle) },
    { id: 'recent', label: t('settings.apiKeys.kpi.recent'), value: counts.recent, tone: 'pending', hint: share(counts.recent) }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const orgs = Array.from(new Set(all.map((r) => r.organisation_id)));
    const scopes = Array.from(new Set(all.flatMap((r) => r.scopes ?? []))).sort();
    // The organisation dropdown lists tenants by name; matching a GUID prefix
    // by eye against another browser tab was the old filter experience.
    const orgOptions = orgs
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      { key: 'organisation', label: t('settings.apiKeys.filters.organisation'), placeholder: t('settings.apiKeys.filters.organisationPlaceholder'), options: orgOptions },
      { key: 'scope', label: t('settings.apiKeys.filters.scope'), placeholder: t('settings.apiKeys.filters.scopePlaceholder'), options: scopes.map((s) => ({ value: s, label: s })) },
      { key: 'status', label: t('common.status'), placeholder: t('settings.apiKeys.filters.statusPlaceholder'), options: [{ value: 'active', label: t('status.active') }, { value: 'revoked', label: t('settings.apiKeys.status.revoked') }] }
    ];
  }, [all, tenants, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;
  function clearAllFilters() { setQuery(''); setKpi('all'); setFilters({}); setPage(1); }

  const filtered = (() => {
    let rows = all;
    // Asking for revoked keys explicitly wins over the display switch, so a
    // KPI or a filter never leads to an empty table it cannot explain.
    const wantsRevoked = kpi === 'revoked' || filters.status === 'revoked';
    if (!showRevoked && !wantsRevoked) rows = rows.filter((r) => r.is_active);
    if (kpi === 'active') rows = rows.filter((r) => r.is_active);
    if (kpi === 'revoked') rows = rows.filter((r) => !r.is_active);
    if (kpi === 'idle') rows = rows.filter(isIdle);
    if (kpi === 'recent') rows = rows.filter(isRecent);
    if (filters.organisation) rows = rows.filter((r) => r.organisation_id === filters.organisation);
    if (filters.scope) rows = rows.filter((r) => (r.scopes ?? []).includes(filters.scope!));
    if (filters.status === 'active') rows = rows.filter((r) => r.is_active);
    if (filters.status === 'revoked') rows = rows.filter((r) => !r.is_active);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      r.prefix.toLowerCase().includes(q) ||
      (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q)
    );
    return rows;
  })();

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  /*
   * The count badge on the Filters button says how many filters are on but not
   * which, so a short list of keys reads the same whether the platform has few
   * keys or a forgotten organisation filter is still applied.
   */
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.status'), value: kpiItems.find((k) => k.id === kpi)?.label ?? kpi });
  }
  if (filters.organisation) {
    pills.push({
      id: 'organisation',
      field: t('settings.apiKeys.filters.organisation'),
      value: tenants.get(filters.organisation)?.name ?? t('tenantName.unknownShort')
    });
  }
  if (filters.scope) pills.push({ id: 'scope', field: t('settings.apiKeys.filters.scope'), value: filters.scope });
  if (filters.status) {
    pills.push({
      id: 'status',
      field: t('common.status'),
      value: filters.status === 'active' ? t('status.active') : t('settings.apiKeys.status.revoked')
    });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') { setQuery(''); return; }
    if (id === 'kpi') { setKpi('all'); return; }
    setFilters((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  function handleRevoke(k: Key) {
    const org = tenants.get(k.organisation_id)?.name;
    openConfirmModal({
      title: t('settings.apiKeys.modal.revoke.title'),
      children: (
        <p style={{ margin: 0 }}>
          {org
            ? t('settings.apiKeys.modal.revoke.bodyWithOrg', { name: k.name, prefix: k.prefix, org })
            : t('settings.apiKeys.modal.revoke.body', { name: k.name })}
        </p>
      ),
      labels: { confirm: t('settings.apiKeys.actions.revoke'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await revoke.mutateAsync(k.id);
          notifySuccess(t('settings.apiKeys.toast.revoked', { name: k.name }), t('settings.apiKeys.toast.revokedTitle'));
        } catch (e) {
          notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.apiKeys.toast.revokeFailedTitle'));
        }
      }
    });
  }

  /*
   * Bulk revoke. Selection is held by key id rather than by row index, so
   * turning a page cannot move it onto different keys, and it is intersected
   * with the filtered set every render: nobody should be able to revoke a key
   * that a filter change has taken off the screen.
   */
  const selectable = paged.filter((k) => k.is_active);
  const selectedKeys = filtered.filter((k) => selected.has(k.id) && k.is_active);
  const allOnPageSelected = selectable.length > 0 && selectable.every((k) => selected.has(k.id));

  function toggleRow(id: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  function togglePage(on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of selectable) { if (on) next.add(k.id); else next.delete(k.id); }
      return next;
    });
  }

  /*
   * The selection now goes to `POST /v1/admin/api-keys/revoke`, which applies
   * the whole batch in one transaction. This used to be one DELETE per key in
   * a loop, so a failure halfway left a selection an operator believed was off
   * half live, and the page could only report the split afterwards. The drawer
   * also collects the reason the endpoint requires, which is stored on every
   * row and written to the security feed with the operator's name.
   */
  const selectedOrgCount = new Set(selectedKeys.map((k) => k.organisation_id)).size;

  /** The rows on screen, as a spreadsheet. Organisation by name, not by id. */
  function exportCsv() {
    const headers = ['name', 'prefix', 'organisation', 'scopes', 'rate_limit_per_minute', 'status', 'created_at', 'last_used_at', 'revoked_at'];
    const lines = [headers.join(',')];
    for (const k of filtered) {
      lines.push([
        k.name,
        k.prefix,
        tenants.get(k.organisation_id)?.name ?? '',
        (k.scopes ?? []).join(' '),
        k.rate_limit_per_minute,
        k.is_active ? 'active' : 'revoked', // i18n-ignore: CSV column value, not prose
        k.created_at,
        k.last_used_at ?? '',
        k.revoked_at ?? ''
      ].map(csvEscape).join(','));
    }
    downloadText(lines.join('\n'), `${t('settings.apiKeys.export.filename')}-${Date.now()}.csv`, 'text/csv');
    notifySuccess(t('settings.apiKeys.toast.exported', { count: filtered.length }), t('settings.apiKeys.toast.exportedTitle'));
  }

  /** Shared by the three views so a revoked key never offers a dead button. */
  const revokeButton = (k: Key) => k.is_active ? (
    <Button
      size="xs"
      variant="subtle"
      color="red"
      loading={revoke.isPending && revoke.variables === k.id}
      onClick={() => handleRevoke(k)}
    >
      {t('settings.apiKeys.actions.revoke')}
    </Button>
  ) : null;

  const scopeOverflow = (k: Key, shown: number) => {
    const rest = (k.scopes ?? []).slice(shown);
    if (rest.length === 0) return null;
    return (
      <Tooltip label={rest.join(', ')} position="top">
        <Text role="caption" c={MUTED}>+{rest.length}</Text>
      </Tooltip>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.apiKeys.title')}
        description={t('settings.apiKeys.description')}
        actions={
          <Group gap="sm">
            <Switch
              label={t('settings.apiKeys.actions.includeRevoked')}
              checked={showRevoked}
              onChange={(next) => { setShowRevoked(next); setPage(1); }}
            />
            {/* The export takes the filtered set rather than the page, because
                the reason to export is to work on the whole selection somewhere
                else. */}
            <Button size="sm" variant="outline" disabled={filtered.length === 0} onClick={exportCsv}>
              {t('action.exportCsv')}
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>{t('settings.apiKeys.actions.new')}</Button>
          </Group>
        }
      />
      <TableShell
        title={t('settings.apiKeys.table.title')}
        description={t('settings.apiKeys.table.description')}
        totalLabel={t('settings.apiKeys.table.total', { shown: filtered.length, total: all.length })}
        kpis={<KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('settings.apiKeys.searchPlaceholder')}
        filters={
          <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
        }
        view={view}
        onViewChange={setView}
        /*
         * The pills belong under the control that set them, which is what this
         * slot is for; they used to be the first child inside the table body.
         * The selection bar sits in the same band, directly above the rows the
         * checkboxes are on.
         */
        belowToolbar={
          <Stack gap="sm">
            <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />
            {selectedKeys.length > 0 && (
              <Group gap="sm" align="center" wrap="wrap">
                <Text role="bodySmall" fw={600}>{t('settings.apiKeys.selection.count', { count: selectedKeys.length })}</Text>
                <Button size="xs" variant="outline" color="red" onClick={() => setRevoking(true)}>
                  {t('settings.apiKeys.selection.revoke', { count: selectedKeys.length })}
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setSelected(new Set())}>
                  {t('settings.apiKeys.selection.clear')}
                </Button>
              </Group>
            )}
          </Stack>
        }
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={<EmptyState title={t('settings.apiKeys.emptyMatch')} description={hasActiveFilters ? t('settings.apiKeys.emptyHint') : t('settings.apiKeys.empty')} action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined} />}
        error={error ? (
          <Alert color="error">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('settings.apiKeys.loadError')}</Text>
              <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        <Stack gap="md">
        {view === 'table' && (
          <DataTable<Indexed<Key>>
            records={paged as Indexed<Key>[]}
            loading={isLoading}
            idAccessor="id"
            emptyState={t('settings.apiKeys.empty')}
            columns={[
              {
                accessor: 'select',
                width: 40,
                // A revoked key has nothing left to revoke, so it offers no
                // checkbox rather than a checkbox that does nothing.
                title: (
                  <Checkbox
                    checked={allOnPageSelected}
                    aria-label={t('settings.apiKeys.select.all')}
                    onChange={togglePage}
                  />
                ),
                render: (r) => r.is_active ? (
                  <Checkbox
                    checked={selected.has(r.id)}
                    aria-label={t('settings.apiKeys.select.aria', { name: r.name })}
                    onChange={(on) => toggleRow(r.id, on)}
                  />
                ) : null
              },
              {
                accessor: 'name',
                title: t('common.name'),
                sortable: true,
                // The key's own prefix is the qualifier that distinguishes two
                // keys a tenant gave the same name.
                render: (r) => <TwoLineCell primary={r.name} secondary={r.prefix} title={r.name} />
              },
              {
                accessor: 'prefix',
                title: t('settings.apiKeys.columns.prefix'),
                render: (r) => <CopyableId value={r.prefix} chars={24} label={t('settings.apiKeys.copyPrefix')} />
              },
              {
                accessor: 'organisation_id',
                title: t('settings.apiKeys.columns.org'),
                render: (r) => {
                  const tenant = tenants.get(r.organisation_id);
                  return <TwoLineCell primary={<TenantName id={r.organisation_id} />} secondary={tenant?.slug} />;
                }
              },
              {
                accessor: 'scopes',
                title: t('settings.apiKeys.columns.scopes'),
                render: (r) => (
                  <Group gap={4}>
                    {(r.scopes ?? []).slice(0, 3).map((s) => (
                      <Badge key={s} size="xs" variant="light">{s}</Badge>
                    ))}
                    {scopeOverflow(r, 3)}
                  </Group>
                )
              },
              {
                accessor: 'rate_limit_per_minute',
                // The gateway does not throttle per key yet, so the column says
                // what the number is: a stored setting, not a live limit.
                title: (
                  <Tooltip label={t('settings.apiKeys.columns.rpmTooltip')} position="top">
                    <span>{t('settings.apiKeys.columns.rpmConfigured')}</span>
                  </Tooltip>
                ),
                textAlign: 'right'
              },
              {
                accessor: 'created_at',
                title: t('settings.apiKeys.columns.created'),
                sortable: true,
                render: (r) => (
                  <span title={formatDateTime(r.created_at)}>
                    <TwoLineCell primary={formatRelative(r.created_at)} secondary={formatDateTime(r.created_at)} />
                  </span>
                )
              },
              {
                accessor: 'last_used_at',
                title: t('settings.apiKeys.columns.lastUsed'),
                sortable: true,
                render: (r) => r.last_used_at
                  ? <span title={formatDateTime(r.last_used_at)}>{formatRelative(r.last_used_at)}</span>
                  : <Text role="caption" c={MUTED}>{t('settings.apiKeys.neverUsed')}</Text>
              },
              {
                accessor: 'is_active',
                title: t('common.status'),
                render: (r) => (
                  <StatusBadge status={r.is_active ? 'healthy' : 'inactive'}>
                    {r.is_active
                      ? t('status.active')
                      : r.revoked_at
                        ? t('settings.apiKeys.revokedOn', { when: formatRelative(r.revoked_at) })
                        : t('settings.apiKeys.status.revoked')}
                  </StatusBadge>
                )
              },
              {
                accessor: 'actions',
                title: '',
                textAlign: 'right',
                // One `…` per row, destructive last and in red, rather than a
                // red button shouting from every line of the table.
                render: (r) => (
                  <RowMenu
                    actions={[
                      { id: 'detail', label: t('settings.apiKeys.actions.provenance'), onSelect: () => setDetailId(r.id) },
                      { id: 'copy', label: t('settings.apiKeys.copyPrefix'), onSelect: () => { void navigator.clipboard?.writeText(r.prefix); } },
                      ...(r.is_active
                        ? [{ id: 'revoke', label: t('settings.apiKeys.actions.revoke'), destructive: true, onSelect: () => handleRevoke(r) }]
                        : [])
                    ]}
                  />
                )
              }
            ]}
          />
        )}
        {view === 'list' && (
          <Stack gap="xs">
            {paged.map((k) => (
              <div key={k.id} className="rr-list-row" style={{ cursor: 'default' }}>
                <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                  <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="baseline" wrap="nowrap">
                      <Text role="subtitle" fw={600} truncate>{k.name}</Text>
                      <CopyableId value={k.prefix} chars={24} label={t('settings.apiKeys.copyPrefix')} />
                    </Group>
                    <Group gap="xs" align="baseline" wrap="nowrap">
                      <TenantName id={k.organisation_id} subtle />
                      <Text role="caption" c={MUTED}>
                        {t('settings.apiKeys.list.meta', { rpm: k.rate_limit_per_minute, lastUsed: k.last_used_at ? formatRelative(k.last_used_at) : t('settings.apiKeys.neverUsed') })}
                      </Text>
                    </Group>
                  </Stack>
                  <Group gap={4} style={{ flexShrink: 0 }} wrap="nowrap">
                    {(k.scopes ?? []).slice(0, 2).map((s) => (
                      <Badge key={s} size="xs" variant="light">{s}</Badge>
                    ))}
                    {scopeOverflow(k, 2)}
                  </Group>
                  <StatusBadge status={k.is_active ? 'healthy' : 'inactive'}>{k.is_active ? t('status.active') : t('settings.apiKeys.status.revoked')}</StatusBadge>
                  {revokeButton(k)}
                </Group>
              </div>
            ))}
          </Stack>
        )}
        {view === 'cards' && (
          <EntityCardGrid<Key>
            records={paged}
            idAccessor="id"
            cols={3}
            spacing="md"
            renderCard={(k) => (
              <Stack gap="md">
                <Group justify="space-between" align="baseline">
                  <Stack gap="xs">
                    <Text role="subtitle" fw={600}>{k.name}</Text>
                    <CopyableId value={k.prefix} chars={24} label={t('settings.apiKeys.copyPrefix')} />
                  </Stack>
                  <StatusBadge status={k.is_active ? 'healthy' : 'inactive'}>{k.is_active ? t('status.active') : t('settings.apiKeys.status.revoked')}</StatusBadge>
                </Group>
                <TenantName id={k.organisation_id} subtle />
                <Group gap={4} wrap="wrap">
                  {(k.scopes ?? []).slice(0, 4).map((s) => (
                    <Badge key={s} size="xs" variant="light">{s}</Badge>
                  ))}
                  {scopeOverflow(k, 4)}
                </Group>
                <Group justify="space-between" align="baseline" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
                  <Text role="caption" c={MUTED}>{t('settings.apiKeys.card.created', { created: formatRelative(k.created_at) })}</Text>
                  <Text role="caption" c={MUTED}>{t('settings.apiKeys.card.lastUsed', { lastUsed: k.last_used_at ? formatRelative(k.last_used_at) : t('settings.apiKeys.neverUsed') })}</Text>
                </Group>
                {revokeButton(k)}
              </Stack>
            )}
          />
        )}
        </Stack>
      </TableShell>

      {/* The RPM column reads like a live limit. It is not one, and the tooltip
          on the header only says so to whoever hovers it. */}
      <Text role="caption" c={MUTED}>{t('settings.apiKeys.footnote.rpm')}</Text>

      <RevokeSelectionDrawer
        opened={revoking}
        onClose={() => setRevoking(false)}
        ids={selectedKeys.map((k) => k.id)}
        orgCount={selectedOrgCount}
        onDone={() => setSelected(new Set())}
      />

      <KeyDetailDrawer id={detailId} onClose={() => setDetailId(null)} />

      <CreateKeyDrawer
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(key) => { setCreating(false); setCreated(key); }}
      />

      {/*
        The plaintext key exists in this browser for as long as this dialog is
        open and nowhere else, ever again, so it closes on an acknowledgement
        rather than on a stray Escape.
      */}
      <OneTimeSecret
        value={created?.key ?? null}
        title={t('settings.apiKeys.created.title')}
        intro={created ? t('settings.apiKeys.created.intro', { name: created.name }) : ''}
        filename={`repletoryreach-api-key-${created?.prefix ?? 'new'}.txt`}
        onAcknowledge={() => setCreated(null)}
      />
    </Stack>
  );
}
